import React, { useState } from "react";
import { FaUser, FaEdit, FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";

const PersonalInformation = ({ driver, editable, phoneVisible = true, phoneEditable, onUpdated, showIfEmpty = false }) => {
  const canEditPhone = phoneVisible && (phoneEditable !== undefined ? phoneEditable : editable);
  const [editMode, setEditMode] = useState({ name: showIfEmpty && !driver.fullName, phone: false });
  const [name, setName] = useState(driver.fullName || "");
  const [phone, setPhone] = useState(driver.phone);
  const token = localStorage.getItem("adminToken");
  const handleNameUpdate = async () => {
    try {
      await axiosInstance.patch(
        `${API.ADMIN.UPDATE_DRIVER}/${driver._id}`,
        {
          fullName: name,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEditMode({ ...editMode, name: false });
      toast.success("Name updated successfully");
      onUpdated();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to update name";
      toast.error(errorMessage);
    }
  };

  const handlePhoneUpdate = async () => {
    // Validate phone number (10 digits)
    if (!/^[0-9]{10}$/.test(phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    try {
      const response = await axiosInstance.patch(
        `${API.ADMIN.UPDATE_DRIVER}/${driver._id}`,
        {
          phone: phone,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Check if the response indicates success
      if (response.data && response.data.success === false) {
        toast.error(response.data.message || "Failed to update phone number");
        return;
      }

      setEditMode({ ...editMode, phone: false });
      toast.success("Phone number updated successfully");
      onUpdated();
    } catch (err) {
      // Handle error response
      const errorMessage =
        err.response?.data?.message || "Failed to update phone number";
      toast.error(errorMessage);
    }
  };

  return (
    <section className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <FaUser className="text-blue-600 text-2xl" />
        <h2 className="text-2xl font-semibold text-gray-800">
          Personal Information
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Name */}
        <div>
          <strong>Full Name:</strong>{" "}
          {editMode.name ? (
            <span className="flex gap-2 items-center">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="border px-2 py-1 rounded text-sm"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={handleNameUpdate}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setEditMode({ ...editMode, name: false });
                  setName(driver.fullName);
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {driver.fullName || (showIfEmpty && editable ? "Click to add" : "N/A")}
              {editable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditMode({ ...editMode, name: true })}
                />
              )}
            </span>
          )}
        </div>

        {/* Phone Number */}
        <div>
          <strong>Phone:</strong>{" "}
          {editMode.phone ? (
            <span className="flex gap-2 items-center">
              <input
                value={phone}
                onChange={(e) => {
                  // Only allow numbers and limit to 10 digits
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setPhone(value);
                }}
                className="border px-2 py-1 rounded text-sm"
                placeholder="Enter 10-digit phone number"
                maxLength="10"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={handlePhoneUpdate}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setEditMode({ ...editMode, phone: false });
                  setPhone(driver.phone);
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {phoneVisible ? driver.phone : "Hidden"}
              {canEditPhone && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditMode({ ...editMode, phone: true })}
                />
              )}
            </span>
          )}
        </div>

        <p>
          <strong>Status:</strong> {driver.status}
        </p>

        {driver.rejectedOrRevisitReasons?.length > 0 && (
          <div className="mt-2">
            <strong>Revisit/Rejected Reasons:</strong>
            <ul className="list-disc ml-5 text-sm text-red-600">
              {driver.rejectedOrRevisitReasons.map((reason, idx) => (
                <li key={idx}>
                  {reason.type}: {reason.statusReason} -{" "}
                  {reason.statusReason || "No reason provided"}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p>
          <strong>ID:</strong> {driver.code}
        </p>
        <p>
          <strong>Wallet Balance:</strong> ₹{driver.walletBalance}
        </p>
        {/*<p>
          <strong>Rating:</strong> {driver.rating}
        </p>*/}
        <p>
          <strong>Created At:</strong>{" "}
          {new Date(driver.createdAt).toLocaleString()}
        </p>
        <p>
          <strong>Updated At:</strong>{" "}
          {new Date(driver.updatedAt).toLocaleString()}
        </p>

        <p>
          <strong>Assigned To:</strong>{" "}
          <span>
            {driver.assignedEmployee
              ? `${driver.assignedEmployee.fullName} (${driver.assignedEmployee.role})`
              : "Not assigned"}
          </span>
        </p>
      </div>
      {/* <div className="mt-4">
        <strong>Assigned To:</strong>{" "}
        {editable ? (
          <span className="flex items-center gap-2">
            <select
              value={driver.assignedEmployee?._id || ""}
              onChange={async (e) => {
                try {
                  await axiosInstance.patch(
                    `${API.ADMIN.UPDATE_DRIVER}/${driver._id}`,
                    { assignedTo: e.target.value },
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                  onUpdated(); // Refresh the driver data
                } catch (err) {
                  alert("Failed to update assignee");
                }
              }}
              className="border px-2 py-1 rounded text-sm"
            >
              <option value="" disabled>
                Select Assignee
              </option>
              {driver.availableEmployees?.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.fullName} ({emp.role})
                </option>
              ))}
            </select>
          </span>
        ) : (
          <span>
            {driver.assignedEmployee
              ? `${driver.assignedEmployee.fullName} (${driver.assignedEmployee.role})`
              : "Not assigned"}
          </span>
        )}
      </div> */}
      
    </section>
  );
};

export default PersonalInformation;
